// English source strings for the device side of module 18 (ADR-025, adapters & edge stream).
export const enAdapters = {
  // ------------------------------------------------------------ API messages
  'adp.api.signingUnavailable': 'Edge command signing is not configured in this deployment, so no command can be sent.',
  'adp.api.agentUnavailable': 'This edge agent is not active.',
  'adp.api.peripheralNotAllowed': 'Allow this device on its edge agent first.',
  'adp.api.notEnrolled': 'This edge agent has not enrolled its key yet.',
  'adp.api.alreadyEnrolled': 'This edge agent is already enrolled with another key. Revoke and pair it again to replace it.',
  'adp.api.replayed': 'This request or command was already used and is refused.',
  'adp.api.nonceMismatch': 'The command reference does not match.',
  'adp.api.commandExpired': 'This command has expired.',
  'adp.api.commandNotCancellable': 'Only a command no agent has acknowledged can be cancelled.',
  'adp.api.notPushSource': 'Only signed-push sources have a secret.',
  'adp.api.sourceDisabled': 'This ANPR source is disabled.',
  'adp.api.anprEntitlement': 'Your organisation does not have ANPR enabled.',
  'adp.api.printJobSettled': 'This print job already has an outcome or a device job.',
  'adp.api.cardNotEncodable': 'Only an available or assigned card can be encoded.',
  'adp.api.cardOnHold': 'This card has an unresolved encoding. Read it back or record the manual check first.',
  'adp.api.cardJobOpen': 'This card already has a device job in progress.',
  'adp.api.holdReleased': 'This hold is already released.',
  'adp.api.updateWithdrawn': 'This update is already withdrawn.',
  'adp.v.wrongSite': 'This device belongs to another site.',
  'adp.v.peripheralUnknown': 'Choose a device attached to an edge agent.',
  'adp.v.capabilityMismatch': 'This device cannot do that.',
  'adp.v.agentUnknown': 'Choose an enrolled edge agent.',
  'adp.v.agentForRelay': 'An edge relay source needs its edge agent, and only a relay source has one.',
  'adp.v.sourceRefTaken': 'This source reference is already used at this site.',
  'adp.v.notContractTestable': 'This adapter has no in-process contract target.',
  'adp.v.versionPublished': 'This version is already published on this channel.',
  // ------------------------------------------------------------ provider refusals (actionable)
  'adp.reject.duplicate_card': 'The access-control system already holds this card for someone else. Use another card or retire the duplicate there.',
  'adp.reject.schedule_unsupported': 'The access-control system cannot hold this schedule. Use a continuous window or narrow the visit.',
  'adp.reject.zone_unknown': 'The access-control system does not know a mapped zone. Check the zone mapping.',
  'adp.reject.card_unknown': 'The access-control system does not know this card. Enrol the card there first.',
  'adp.reject.token_type_unsupported': 'The access-control system does not support this credential type.',
  // ------------------------------------------------------------ limitations
  'adp.limit.untestedWithVendor': 'Standards-based, tested only against a local fake. Not validated with any vendor system.',
  'adp.limit.profileRequired': 'The access-control side must implement the passage-access-rest/1 profile.',
  'adp.limit.noProduction': 'Not allowed in production until the owner validates a specific platform and version.',
  'adp.limit.noEntryCommands': 'Plate reads are observations only; they never open a barrier or decide entry.',
  'adp.limit.simulatorOnly': 'Simulator for development and tests only.',
  'adp.limit.unavailable': 'Unavailable: no adapter exists and no compatibility test has passed.',
  // ------------------------------------------------------------ UI (apps/web/src/pages/IntegrationsDevices.tsx)
  'ig.tab.edge': 'Edge agents & jobs',
  'ig.tab.anpr': 'ANPR sources',
  'ig.tab.catalogue': 'Adapter catalogue',
  'adp.ui.secretTitle': 'ANPR push secret',
  'adp.ui.edge.title': 'Edge agents',
  'adp.ui.edge.intro': 'Local agents drive printers, encoders and readers that a browser cannot. Connectivity is shown apart from job outcomes.',
  'adp.ui.edge.empty': 'No edge agent is paired. Pair one from the Devices tab with the kind "Edge agent".',
  'adp.ui.edge.agent': 'Agent',
  'adp.ui.edge.commands': 'Commands',
  'adp.ui.edge.buffer': 'Offline buffer',
  'adp.ui.edge.fingerprint': 'Key',
  'adp.ui.edge.pending': '{count} pending',
  'adp.ui.edge.unknownCount': '{count} with unknown outcome',
  'adp.ui.edge.depth': '{count} buffered',
  'adp.ui.edge.dropped': '{count} refused while full',
  'adp.ui.edge.detailHint': 'Allowed devices, signed commands and the update channel.',
  'adp.ui.edge.rotated': 'Rotated {at}',
  'adp.ui.edge.version': 'Agent version',
  'adp.ui.edge.update': 'Update channel',
  'adp.ui.edge.updateAvailable': 'Version {version} is available and its signature verified. Install it locally.',
  'adp.ui.edge.updateCurrent': 'Up to date (latest signed version {version}).',
  'adp.ui.edge.updateUnverified': 'Version {version} was offered but its signature did not verify. Nothing will be installed.',
  'adp.ui.edge.updateNone': 'No update checked yet.',
  'adp.ui.edge.peripherals': 'Attached devices',
  'adp.ui.edge.peripheralsHint': 'A device reported by an agent does nothing until a person allows it. Firmware not on a validated list is labelled unvalidated.',
  'adp.ui.edge.firmwareValidated': 'Firmware validated',
  'adp.ui.edge.firmwareUnvalidated': 'Firmware unvalidated',
  'adp.ui.edge.allow': 'Allow',
  'adp.ui.edge.block': 'Block',
  'adp.ui.edge.testPrint': 'Print test page',
  'adp.ui.edge.ping': 'Send test command',
  'adp.ui.edge.commandLog': 'Recent signed commands',
  'adp.ui.edge.commandHint': 'Each command is signed, expires and runs once. A command the device did not confirm stays unknown.',
  'adp.ui.edge.late': 'reported late',
  'adp.ui.jobs.title': 'Device jobs',
  'adp.ui.jobs.intro': 'Print, encode and identity-read jobs and their own outcomes. An online device is not a successful job.',
  'adp.ui.jobs.empty': 'No device jobs yet.',
  'adp.ui.jobs.job': 'Job',
  'adp.ui.jobs.outcome': 'Outcome',
  'adp.ui.jobs.when': 'Requested',
  'adp.ui.jobs.held': 'Card on hold',
  'adp.ui.holds.title': 'Cards on hold',
  'adp.ui.holds.intro': 'A card whose encoding outcome is unknown cannot be reused until it is read back or checked by hand.',
  'adp.ui.holds.empty': 'No card is on hold.',
  'adp.ui.holds.manual': 'Record manual check',
  'adp.ui.holds.manualHint': 'Only after the card was checked or wiped at the encoder. This is recorded with your name.',
  'adp.ui.holds.note': 'What was checked',
  'adp.ui.holds.release': 'Release the card',
  'adp.ui.updates.title': 'Agent updates',
  'adp.ui.updates.intro': 'Signed version manifests. Agents verify the signature and the file digest; nothing is downloaded or installed automatically.',
  'adp.ui.updates.key': 'Signed with key {key}.',
  'adp.ui.updates.noKey': 'No signing key is configured in this deployment.',
  'adp.ui.updates.empty': 'No update published.',
  'adp.ui.updates.channel': 'Channel',
  'adp.ui.updates.version': 'Version',
  'adp.ui.updates.sha': 'SHA-256 of the build',
  'adp.ui.updates.size': 'Size in bytes',
  'adp.ui.updates.publish': 'Publish signed manifest',
  'adp.ui.anpr.title': 'ANPR sources',
  'adp.ui.anpr.intro': 'Camera systems feed plate reads into the gate review. Unclear, low-confidence or clock-skewed reads go to a guard.',
  'adp.ui.anpr.empty': 'No ANPR source yet.',
  'adp.ui.anpr.source': 'Source',
  'adp.ui.anpr.transport': 'Connection',
  'adp.ui.anpr.last24': 'Last 24 hours',
  'adp.ui.anpr.secretHint': 'Secret ending {hint}',
  'adp.ui.anpr.stats': '{recorded} recorded · {duplicates} repeated · {rejected} rejected',
  'adp.ui.anpr.skewed': '{count} with camera clock skew',
  'adp.ui.anpr.events': 'Recent events',
  'adp.ui.anpr.add': 'Add ANPR source',
  'adp.ui.anpr.addHint': 'A signed-push source gets a secret shown once. A relay source is bound to one edge agent.',
  'adp.ui.anpr.name': 'Name',
  'adp.ui.anpr.ref': 'Source reference',
  'adp.ui.anpr.refHint': 'Lowercase letters, digits, dots and dashes. Used to tell camera systems apart.',
  'adp.ui.anpr.agent': 'Edge agent ID',
  'adp.ui.anpr.scale': 'Confidence scale',
  'adp.ui.anpr.skew': 'Clock skew tolerance (seconds)',
  'adp.ui.anpr.skewHint': 'Reads from a camera clock further off than this are flagged and never count as a confident match.',
  'adp.ui.anpr.create': 'Add source',
  'adp.ui.anpr.eventsHint': 'Routing facts only. Plate text is shown in the gate workspace.',
  'adp.ui.anpr.noEvents': 'No events yet.',
  'adp.ui.anpr.skewFlag': 'clock skew {seconds} s',
  'adp.ui.anpr.lateFlag': 'late',
  'adp.ui.cat.title': 'Adapter catalogue',
  'adp.ui.cat.intro': 'Every adapter, its version, what it can do and how far it has been tested.',
  'adp.ui.cat.noVendor': 'No vendor system is validated. Named platforms stay unavailable until the owner chooses one and its compatibility tests pass.',
  'adp.ui.cat.adapter': 'Adapter',
  'adp.ui.cat.status': 'Validation',
  'adp.ui.cat.capabilities': 'Operations and capabilities',
  'adp.ui.cat.contract': 'Contract tests',
  'adp.ui.cat.validatedVersions': 'Validated versions: {versions}',
  'adp.ui.cat.none': 'none',
  'adp.ui.cat.lastRun': '{passed} passed, {failed} failed',
  'adp.ui.cat.neverRun': 'Not run yet',
  'adp.ui.cat.run': 'Run contract tests',
  'adp.ui.cat.resultTitle': 'Contract tests: {adapter}',
  'adp.ui.cat.targetFake': 'Run in-process against a local fake. Not a test against any vendor system.',
  'adp.ui.cat.targetSimulator': 'Run in-process against the simulator.',
  'adp.ui.cat.passed': 'Passed',
  'adp.ui.cat.failed': 'Failed',
  'adp.validation.simulator': 'Simulator only',
  'adp.validation.standards-based-untested-with-vendor': 'Standards-based, untested with any vendor',
  'adp.validation.unavailable': 'Unavailable',
  'adp.periph.pending': 'Waiting for a decision',
  'adp.periph.allowed': 'Allowed',
  'adp.periph.blocked': 'Blocked',
  'adp.kind.printer': 'Badge printer',
  'adp.kind.encoder': 'Card encoder',
  'adp.kind.id_reader': 'Emirates ID reader',
  'adp.kind.mrz_reader': 'MRZ reader',
  'adp.kind.anpr_camera': 'ANPR camera',
  'adp.kind.osdp_reader': 'Door reader (OSDP)',
  'adp.kindAdapter.access_control': 'Access control',
  'adp.kindAdapter.anpr': 'ANPR',
  'adp.kindAdapter.reader': 'Identity reader',
  'adp.kindAdapter.printer': 'Printer',
  'adp.kindAdapter.encoder': 'Card encoder',
  'adp.cmd.diagnostic.ping': 'Test command',
  'adp.cmd.printer.test': 'Test page',
  'adp.cmd.print.badge': 'Print badge',
  'adp.cmd.encode.card': 'Encode card',
  'adp.cmd.encode.read_back': 'Read card back',
  'adp.cmd.reader.capture': 'Read identity document',
  'adp.cmdStatus.issued': 'Waiting for the agent',
  'adp.cmdStatus.acknowledged': 'Received by the agent',
  'adp.cmdStatus.completed': 'Completed',
  'adp.cmdStatus.failed': 'Failed',
  'adp.cmdStatus.unknown': 'Outcome unknown',
  'adp.cmdStatus.rejected_by_agent': 'Refused by the agent',
  'adp.cmdStatus.expired': 'Expired before delivery',
  'adp.cmdStatus.cancelled': 'Cancelled',
  'adp.job.print': 'Badge print',
  'adp.job.encode': 'Card encoding',
  'adp.job.encode_read_back': 'Card read-back',
  'adp.job.read_identity': 'Identity document read',
  'adp.jobStatus.queued': 'Queued',
  'adp.jobStatus.sent': 'Sent to the device',
  'adp.jobStatus.printed': 'Printed',
  'adp.jobStatus.encoded': 'Encoded',
  'adp.jobStatus.read': 'Read',
  'adp.jobStatus.failed': 'Failed',
  'adp.jobStatus.unknown': 'Outcome unknown',
  'adp.jobStatus.cancelled': 'Cancelled',
  'adp.reason.not_delivered': 'Not delivered before it expired; nothing ran.',
  'adp.reason.no_result_before_deadline': 'The device did not confirm in time.',
  'adp.reason.device_confirmed': 'Confirmed by the device.',
  'adp.reason.device_failed': 'The device reported a failure.',
  'adp.reason.device_unconfirmed': 'The device did not confirm.',
  'adp.reason.rejected_by_agent': 'The agent refused the command.',
  'adp.reason.reader_output_invalid': 'The reader output could not be used.',
  'adp.reason.cancelled_by_operator': 'Cancelled by an operator.',
  'adp.reason.invalid_payload': 'The event was not in the expected format.',
  'adp.reason.source_not_bound_to_agent': 'This agent may not relay for the source.',
  'adp.reason.read_back_blank': 'Read back: card is blank.',
  'adp.reason.read_back_encoded': 'Read back: card carries data.',
  'adp.hold.encode_unknown': 'Encoding outcome unknown',
  'adp.hold.read_back_unknown': 'Read-back outcome unknown',
  'adp.channel.stable': 'Stable',
  'adp.channel.pilot': 'Pilot',
  'adp.manifest.published': 'Published',
  'adp.manifest.withdrawn': 'Withdrawn',
  'adp.transport.http_push': 'Signed HTTP push',
  'adp.transport.edge_relay': 'Edge agent relay',
  'adp.sourceStatus.active': 'Active',
  'adp.sourceStatus.disabled': 'Disabled',
  'adp.scale.unit': '0 to 1',
  'adp.scale.percent': '0 to 100',
  'adp.evOutcome.recorded': 'Recorded',
  'adp.evOutcome.duplicate': 'Repeat',
  'adp.evOutcome.rejected': 'Rejected',
  'adp.op.issue': 'Issue',
  'adp.op.revoke': 'Revoke',
  'adp.op.read_back': 'Read back',
  'adp.op.normalise_read': 'Normalise plate reads',
  'adp.op.print': 'Print',
  'adp.op.test_page': 'Test page',
  'adp.op.encode': 'Encode',
  'adp.op.capture': 'Capture',
  'adp.adapter.access.rest-json': 'Generic REST/JSON access control (passage-access-rest/1)',
  'adp.adapter.anpr.generic-event': 'Generic ANPR event ingest',
  'adp.adapter.printer.sim-edge': 'Badge printer simulator (edge agent)',
  'adp.adapter.encoder.sim-edge': 'Card encoder simulator (edge agent)',
  'adp.adapter.reader.sim-eid-edge': 'Emirates ID reader simulator (edge agent)',
  'adp.adapter.reader.sim-mrz-edge': 'MRZ reader simulator (edge agent)',
  'adp.case.manifest.valid': 'Versioned manifest; nothing claims production use or vendor validation.',
  'adp.case.operation.unsupported_explicit': 'An unsupported operation returns an explicit unsupported result.',
  'adp.case.operation.timeout_unknown': 'No answer in time is an unknown outcome, never success.',
  'adp.case.operation.idempotent_replay': 'Repeating with the same idempotency key does not act twice.',
  'adp.case.health.separate_from_outcome': 'A healthy connection cannot hide a failed or unknown outcome.',
  'adp.case.config.secret_references': 'Plaintext secrets are refused; only references are accepted.',
  'adp.case.access.issue_read_back': 'An issued credential reads back active with its scope.',
  'adp.case.access.duplicate_card_blocked': 'A duplicate card at the provider blocks provisioning with a clear reason.',
  'adp.case.access.schedule_restriction_blocked': 'A schedule the provider cannot hold blocks provisioning with a clear reason.',
  'adp.case.access.revoke_read_back': 'A confirmed revocation reads back as revoked.',
  'adp.case.access.server_error_unknown_then_read_back': 'A provider error after applying is unknown; read-back finds the real state.',
  'adp.case.access.signed_requests': 'Every request is signed; without the secret nothing is sent.',
  'adp.case.anpr.low_confidence_preserved': 'Low confidence stays low.',
  'adp.case.anpr.clock_skew_flagged': 'A skewed camera clock is flagged and never counts as a present, confident read.',
  'adp.case.anpr.jurisdiction_normalised': 'Country and emirate codes are normalised.',
  'adp.case.anpr.invalid_rejected': 'Invalid events are rejected, never guessed.',
  'adp.case.printer.jam_not_success': 'A jammed job is not a successful print.',
  'adp.case.encoder.no_custody_or_credential': 'Encoding never changes custody or the credential.',
  'adp.case.encoder.read_back_after_unknown': 'After an encoder timeout, read-back shows what the card carries.',
  'adp.case.reader.firmware_unvalidated': 'Unknown firmware is labelled unvalidated.',
  'adp.case.reader.output_is_evidence_only': 'Reader output is evidence for review, never a verified identity.',
  'adp.ui.edge.signedWith': 'signed with {key}',
  'adp.reason.paper_jam': 'The printer reported a paper jam.',
  'adp.reason.card_not_present': 'No card in the encoder.',
  'adp.reason.reader_error': 'The reader reported an error.',
  'adp.reason.printer_no_confirmation': 'The printer did not confirm.',
  'adp.reason.write_unverified': 'The encoder could not confirm the write.',
  'adp.reason.device_timeout': 'The device did not answer in time.',
  'adp.reason.device_error': 'The device driver failed.',
  'adp.reason.peripheral_missing': 'The device is not attached to the agent.',
  'adp.reason.unsupported_command': 'The agent does not support this command.',
  'adp.reason.expired': 'The command had expired.',
  'adp.reason.replayed': 'The command had already been used.',
  'adp.reason.wrong_site': 'The command named another site.',
  'adp.reason.capability_not_allowed': 'The device is not allowed to do this.',
  'adp.reason.peripheral_unknown': 'The device is not allowed on this agent.',
  'adp.reason.ttl_too_long': 'The command lifetime was not acceptable.',
  'adp.reason.not_yet_valid': 'The command was not valid yet.',
  'adp.reason.capability_mismatch': 'The command did not match its capability.',
  'adp.reason.bad_version': 'Unknown command format.',
  'adp.reason.observed_in_future': 'The read is too far in the future.',
  'adp.reason.confidence_out_of_range': 'The confidence value is out of range.',
  'adp.reason.source_missing': 'The relayed event names no source.',
  'adp.reason.source_disabled': 'The source is disabled.',
  'adp.reason.entitlement_required': 'ANPR is not enabled for the organisation.',
  'adp.reason.read_back_invalid': 'The read-back answer could not be used.',
  'adp.reason.cancelled_before_delivery': 'Cancelled before any agent received it.',
};
